import { enhancedApi } from '@store/generated/stedi';

/**
 * Feature-specific tag types for Stedi connection cache management
 */
const STEDI_TAG_TYPES = ['StediConnection', 'StediStatus'] as const;

/**
 * Stedi Connection API - extends the generated OpenAPI client
 * This ensures we're using generated types and endpoints
 * while maintaining feature-specific customization
 *
 * Supports both:
 * - Admin routes: /v2/admin/orgs/{organizationId}/stedi/* (for JotPsych admins)
 * - Provider routes: /v2/provider/stedi/* (for billing administrators)
 */
export const stediConnectionApi = enhancedApi.enhanceEndpoints({
  addTagTypes: STEDI_TAG_TYPES,
  endpoints: {
    // ========================================
    // Admin endpoints (requires JotPsych admin role)
    // ========================================
    getV2AdminOrgsByOrganizationIdStediStatus: {
      providesTags: (_result, _error, arg) => [
        { type: 'StediStatus', id: arg.organizationId },
        { type: 'StediConnection', id: arg.organizationId },
      ],
    },

    postV2AdminOrgsByOrganizationIdStediConnect: {
      invalidatesTags: (_result, _error, arg) => [
        { type: 'StediStatus', id: arg.organizationId },
        { type: 'StediConnection', id: arg.organizationId },
      ],
    },

    postV2AdminOrgsByOrganizationIdStediVerify: {
      invalidatesTags: (_result, _error, arg) => [
        { type: 'StediStatus', id: arg.organizationId },
      ],
    },

    deleteV2AdminOrgsByOrganizationIdStediDisconnect: {
      invalidatesTags: (_result, _error, arg) => [
        { type: 'StediStatus', id: arg.organizationId },
        { type: 'StediConnection', id: arg.organizationId },
      ],
    },

    // ========================================
    // Provider endpoints (requires billing_administrator role)
    // ========================================
    getV2ProviderStediStatus: {
      providesTags: () => [
        { type: 'StediStatus', id: 'PROVIDER' },
        { type: 'StediConnection', id: 'PROVIDER' },
      ],
    },

    postV2ProviderStediConnect: {
      invalidatesTags: () => [
        { type: 'StediStatus', id: 'PROVIDER' },
        { type: 'StediConnection', id: 'PROVIDER' },
      ],
    },

    postV2ProviderStediVerify: {
      invalidatesTags: () => [{ type: 'StediStatus', id: 'PROVIDER' }],
    },

    deleteV2ProviderStediDisconnect: {
      invalidatesTags: () => [
        { type: 'StediStatus', id: 'PROVIDER' },
        { type: 'StediConnection', id: 'PROVIDER' },
      ],
    },
  },
});

// ========================================
// Admin hooks (for JotPsych admin panel)
// ========================================
export const {
  useGetV2AdminOrgsByOrganizationIdStediStatusQuery: useGetStediStatusAdminQuery,
  useLazyGetV2AdminOrgsByOrganizationIdStediStatusQuery: useLazyGetStediStatusAdminQuery,
  usePostV2AdminOrgsByOrganizationIdStediConnectMutation: useConnectStediAdminMutation,
  usePostV2AdminOrgsByOrganizationIdStediVerifyMutation: useVerifyStediAdminMutation,
  useDeleteV2AdminOrgsByOrganizationIdStediDisconnectMutation: useDisconnectStediAdminMutation,
} = stediConnectionApi;

// ========================================
// Provider hooks (for billing administrators)
// ========================================
export const {
  useGetV2ProviderStediStatusQuery: useGetStediStatusProviderQuery,
  useLazyGetV2ProviderStediStatusQuery: useLazyGetStediStatusProviderQuery,
  usePostV2ProviderStediConnectMutation: useConnectStediProviderMutation,
  usePostV2ProviderStediVerifyMutation: useVerifyStediProviderMutation,
  useDeleteV2ProviderStediDisconnectMutation: useDisconnectStediProviderMutation,
} = stediConnectionApi;

// Legacy exports for backward compatibility (point to admin hooks)
// TODO: Remove after migrating all usages
export const useGetStediStatusQuery = useGetStediStatusAdminQuery;
export const useLazyGetStediStatusQuery = useLazyGetStediStatusAdminQuery;
export const useConnectStediMutation = useConnectStediAdminMutation;
export const useVerifyStediMutation = useVerifyStediAdminMutation;
export const useDisconnectStediMutation = useDisconnectStediAdminMutation;
